import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import { authAction, firmsAction } from 'common/actions';
import { ERROR_403 } from 'common/config/api';
import { useStore } from 'common/store';
import { FIRM_LIST_STATUSES } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import CompanySelector from 'pages/Auth/components/CompanySelector';
import type { CompanySelectorPermission } from 'pages/Auth/components/CompanySelector/CompanySelector.props';
import type { LoginFirm } from 'pages/Auth/components/FirmList';
import FirmSelector from 'pages/Auth/components/FirmSelector';
import { useGetCompanyInfoById } from 'services/hooks/company';
import { useGetFundById } from 'services/hooks/fund';
import { useWorkspaceAutoRedirects } from './hooks/useWorkspaceAutoRedirects';
import type { WorkspaceSelectorProps } from './WorkspaceSelector.props';

const WorkspaceSelector = ({
  isCompanyProduct = false,
  lowerPermissions = [],
  standaloneCompaniesPermissions,
}: WorkspaceSelectorProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const getCompanyInfo = useGetCompanyInfoById();
  const [, getFundInfo] = useGetFundById();
  const [{ firmList, firmListStatus = FIRM_LIST_STATUSES.IDLE, user }, dispatch] = useStore() as UseStoreValues;
  const hasResolvedFirmBootstrap = firmListStatus !== FIRM_LIST_STATUSES.IDLE || !isEmpty(firmList);
  const [isLoading, setIsLoading] = useState(() => !hasResolvedFirmBootstrap);
  const hasRequestedFirmListRef = useRef(false);

  const resolvedUser = useMemo(
    () =>
      user
        ? {
          id: user.id,
          is_staff: user.is_staff,
          is_superuser: user.is_superuser,
        }
        : undefined,
    [user]
  );
  const resolvedFirmList = (firmList as LoginFirm[] | null | undefined) ?? null;
  const resolvedStandaloneCompaniesPermissions = useMemo(
    () =>
      (standaloneCompaniesPermissions ?? user?.standalone_companies_permissions ?? []).filter(
        (company): company is CompanySelectorPermission =>
          Boolean(company.id != null && company.slug)
      ),
    [standaloneCompaniesPermissions, user?.standalone_companies_permissions]
  );
  const hasNotFirmPermissions = useMemo(
    () => Boolean(resolvedFirmList?.every(firm => firm.user_permissions?.length === 0) && resolvedUser && !resolvedUser.is_superuser),
    [resolvedFirmList, resolvedUser]
  );
  const redirectPlan = useWorkspaceAutoRedirects({
    fetchCompanyInfo: getCompanyInfo,
    fetchFundInfo: getFundInfo,
    hasNotFirmPermissions,
    isCompanyProduct,
    locationState: location.state,
    lowerPermissions,
    markRestrictedUser: () => dispatch(authAction.setIsRestrictedUser(true)),
    navigate,
    resolvedFirmList,
    resolvedStandaloneCompaniesPermissions,
    resolvedUser,
  });
  const isRedirectPending = Boolean(redirectPlan && redirectPlan.kind !== 'clear-app-auto-select');

  const fetchFirmList = useCallback(async () => {
    try {
      setIsLoading(true);
      const { firms } = await firmsAction.getFirmList();

      if (firms === ERROR_403) {
        dispatch(firmsAction.setFirmList(null));
        dispatch(firmsAction.setFirmListStatus(FIRM_LIST_STATUSES.FORBIDDEN));
      } else {
        dispatch(firmsAction.setFirmList(firms));
        dispatch(firmsAction.setFirmListStatus(FIRM_LIST_STATUSES.READY));
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      throw new Error(error as string);
    }
  }, [dispatch]);

  // Workspace selection only needs the firm bootstrap once per mount; store updates and redirects can
  // rerender this screen several times while the auth shell resolves the final destination.
  useEffect(() => {
    if (hasRequestedFirmListRef.current) {
      return;
    }

    hasRequestedFirmListRef.current = true;

    // Reuse the auth bootstrap when it already resolved the firm list so product selection does not
    // immediately show a second loading state before rendering the selector or redirecting onward.
    if (firmListStatus !== FIRM_LIST_STATUSES.IDLE) {
      setIsLoading(false);
      return;
    }

    if (isEmpty(firmList)) {
      fetchFirmList();
      return;
    }

    setIsLoading(false);
  }, [fetchFirmList, firmList, firmListStatus]);

  if (isCompanyProduct) {
    return <CompanySelector companies={resolvedStandaloneCompaniesPermissions} isLoading={isLoading || isRedirectPending} />;
  }

  if (isEmpty(resolvedFirmList) && (isLoading || resolvedStandaloneCompaniesPermissions.length > 0)) {
    return <CompanySelector companies={resolvedStandaloneCompaniesPermissions} isLoading={isLoading} />;
  }

  return (
    <FirmSelector
      firmList={resolvedFirmList}
      firmListStatus={firmListStatus}
      isLoading={isLoading || isRedirectPending}
      user={resolvedUser ? { is_superuser: resolvedUser.is_superuser } : null}
    />
  );
};

export default WorkspaceSelector;
